import { useEffect } from "react";
import { shallowEqual, useDispatch, useSelector } from "react-redux";
import { CLEAR_REGISTER_PUSH_NOTIFICATIONS } from "../actions/types";
import { getPushNotificationsSelector } from "../selectors";
import {
	canRegisterSilently,
	requiresUserGesture,
	setBadge
} from "../utils/WebPush";
import {
	getMessaging,
	getPushServiceWorkerRegistration,
	registerWebPush
} from "./WebPushRegistration";

export default function PushNotifications() {
	const dispatch = useDispatch();
	const { registerForPushNotifications, lang } = useSelector(
		getPushNotificationsSelector,
		shallowEqual
	);
	const { totalNotRead } = useSelector(state => state.chatReducer);
	const { cptNewEvents } = useSelector(state => state.liveReducer);

	useEffect(() => {
		if (!registerForPushNotifications) {
			return;
		}
		// On WebKit the permission prompt is only honoured inside a user gesture
		// and this runs from an effect, so the prompt is left to
		// `EnableNotificationsPrompt` and to the settings row. Chromium and Gecko
		// keep prompting automatically, as they always have.
		if (!canRegisterSilently() && requiresUserGesture()) {
			dispatch({ type: CLEAR_REGISTER_PUSH_NOTIFICATIONS });
			return;
		}
		registerWebPush(dispatch, lang, {
			promptIfNeeded: !requiresUserGesture()
		});
	}, [registerForPushNotifications, lang, dispatch]);

	useEffect(() => {
		const messaging = getMessaging();
		if (!messaging) {
			return undefined;
		}
		// Attaching the callback needs no permission, and doing it unconditionally
		// means a user who grants it from the banner starts receiving foreground
		// messages straight away rather than on the next launch.
		// The service worker forwards the payload here instead of showing a
		// notification whenever a window is visible, so the notification is raised
		// on this side.
		return messaging.onMessage(payload => {
			const { title, body, tag, image } = payload?.notification ?? {};
			if (!title) {
				return;
			}
			getPushServiceWorkerRegistration()
				.then(registration =>
					registration?.showNotification(title, {
						body,
						tag,
						// Optional hero image (webpush.notification.image on the
						// server side). Shown on Android, ignored on iOS PWAs.
						...(image ? { image } : {})
					})
				)
				.catch(error => console.log("Unable to show the notification", error));
		});
	}, []);

	// Mirrors the native badge (see AppNavigationContainer). Sum of unread
	// messages + unseen news-feed posts. Only the app shell can clear it once
	// the user has actually seen either, so it is driven from the store rather
	// than from the push payload alone.
	//
	// Resyncing on every return to the foreground matters as much as reacting to
	// the value itself: the service worker has been counting notifications on
	// its own meanwhile, and the sum alone would not fire when the two ended up
	// on the same number for different reasons.
	useEffect(() => {
		const badge = (totalNotRead || 0) + (cptNewEvents || 0);
		setBadge(badge);

		const resync = () => {
			if (document.visibilityState === "visible") {
				setBadge(badge);
			}
		};
		document.addEventListener("visibilitychange", resync);
		return () => document.removeEventListener("visibilitychange", resync);
	}, [totalNotRead, cptNewEvents]);

	return null;
}
